import * as Immutable from 'immutable';
import type { ID, ReduxAction } from '@repo/utils/types';
import Occasion from '@repo/utils/helpers/Occasion';
import AppState from '../app-state';
import Activity, {
  ActivityItem,
  ActivityItemType,
  FilteredActivity,
} from '@repo/utils/models/Activity';
import Contact from '@repo/utils/models/Contact';
import { selectGroupActivityItem } from './groupActivity';

export const selectAllActivity = (state: AppState) => state.data.activity.all;
const selectFilteredActivity = (state: AppState) => state.data.activity.filtered;
export const selectActiveActivityFilter = (state: AppState) => state.data.activity.activeFilter;

export const selectActivity = (state: AppState) => {
  if (selectActiveActivityFilter(state)) {
    return selectFilteredActivity(state);
  }
  return selectAllActivity(state);
};

export const selectActivityHasNewItems = (state: AppState) => {
  const seenItems = state.data.activity.seenItems;
  return state.data.activity.all.items.some(
    (item) => item?.seen === false && !seenItems.has(item.id),
  );
};

export const selectAvailableActivityFilters = (state: AppState) =>
  state.data.activity.availableFilters;

export const selectActivityItem = (activityItemID: ID) => (state: AppState) =>
  state.data.activity.all.items.get(activityItemID) ||
  state.data.activity.filtered.items.get(activityItemID) ||
  selectGroupActivityItem(activityItemID)(state);

export const selectActivityItemCount = (state: AppState) => {
  const filters = selectAvailableActivityFilters(state);
  const activeFilter = selectActiveActivityFilter(state);

  if (!activeFilter) {
    return filters?.reduce((total, filter) => total + filter.count, 0) || 0;
  }

  return filters?.find((filter) => filter.type === activeFilter)?.count || 0;
};

export const handleContactActions = (
  state: BaseActivityState,
  actionType: string,
  payload: any,
) => {
  // These next few actions are all specifically intended to handle
  // latency issues w/r/t elasticsearch updating following a successful update to the Clay API.
  // TODO: we should handle expiring the cache of archived and merged contacts
  switch (actionType) {
    case 'DELETE_CONTACT_REQUESTED':
      return {
        ...state,
        all: state.all.copyWith({
          archivedContactIDs: state.all.archivedContactIDs.add(payload.id),
        }),
        filtered: state.filtered.copyWith({
          archivedContactIDs: state.filtered.archivedContactIDs.add(payload.id),
        }),
      };

    case 'DELETE_CONTACT_FAILED':
      return {
        ...state,
        all: state.all.copyWith({
          archivedContactIDs: state.all.archivedContactIDs.delete(payload.id),
        }),
        filtered: state.filtered.copyWith({
          archivedContactIDs: state.filtered.archivedContactIDs.delete(payload.id),
        }),
      };

    case 'DELETE_CONTACT_BATCH_REQUESTED':
      return {
        ...state,
        all: state.all.copyWith({
          archivedContactIDs: state.all.archivedContactIDs.union(payload.contact_ids),
        }),
        filtered: state.filtered.copyWith({
          archivedContactIDs: state.filtered.archivedContactIDs.union(payload.contact_ids),
        }),
      };

    case 'DELETE_CONTACT_BATCH_FAILED':
      return {
        ...state,
        all: state.all.copyWith({
          archivedContactIDs: state.all.archivedContactIDs.subtract(payload.contact_ids),
        }),
        filtered: state.filtered.copyWith({
          archivedContactIDs: state.filtered.archivedContactIDs.subtract(payload.contact_ids),
        }),
      };

    case 'EXPIRE_DELETED_CONTACTS_CACHE':
      return {
        ...state,
        deletedIDCacheToExpire: state.deletedIDCacheToExpire.union(payload.contact_ids),
      };
    case 'EXPIRE_MERGED_CONTACTS_CACHE':
      return {
        ...state,
        deletedIDCacheToExpire: state.deletedIDCacheToExpire.union(payload.mergedContactIDs),
        mergedIDCacheToExpire: state.mergedIDCacheToExpire.add(
          payload?.response?.contact?.objectID ||
            payload?.response?.contact?.id ||
            payload?.contact?.id,
        ),
      };

    case 'RESTORE_ARCHIVED_CONTACT_BATCH_REQUESTED':
      return {
        ...state,
        all: state.all.copyWith({
          archivedContactIDs: state.all.archivedContactIDs.subtract(payload.contactIDs || []),
        }),
        filtered: state.filtered.copyWith({
          archivedContactIDs: state.filtered.archivedContactIDs.subtract(payload.contactIDs || []),
        }),
      };

    case 'MERGE_CONTACT_BATCH_SUCCEEDED':
      const mergedContact = new Contact(payload?.response?.contact || payload?.contact);
      return {
        ...state,
        all: state.all.addMergedContact(mergedContact, Immutable.Set(payload.mergedContactIDs)),
        filtered: state.filtered.addMergedContact(
          mergedContact,
          Immutable.Set(payload.mergedContactIDs),
        ),
      };
  }

  return undefined;
};

export interface BaseActivityState {
  all: Activity;
  filtered: FilteredActivity;
  activeFilter: ActivityItemType;
  deletedIDCacheToExpire: Immutable.Set<ID>;
  mergedIDCacheToExpire: Immutable.Set<ID>;
}

export interface ActivityState extends BaseActivityState {
  availableFilters?: { type: ActivityItemType; count: number }[];
  dismissedItems: Immutable.Set<ID>;
  seenItems: Immutable.Set<ID>;
}

const initialActivityState = {
  all: new Activity({ limit: 50 }),
  filtered: new FilteredActivity({ limit: 50 }),
  activeFilter: undefined,
  availableFilters: [],
  dismissedItems: Immutable.Set<ID>(),
  seenItems: Immutable.Set<ID>(),
  deletedIDCacheToExpire: Immutable.Set<ID>(),
  mergedIDCacheToExpire: Immutable.Set<ID>(),
};

export function activityReducer(
  state: ActivityState = initialActivityState,
  { type, payload }: ReduxAction,
) {
  const targetCollectionKey = payload?.filter ? 'filtered' : 'all';
  const targetCollection = state[targetCollectionKey];
  const otherCollectionKey = targetCollectionKey === 'all' ? 'filtered' : 'all';
  const otherCollection = state[otherCollectionKey];

  const stateChangeFromContactAction = handleContactActions(state, type, payload);

  if (!!stateChangeFromContactAction) {
    return stateChangeFromContactAction;
  }

  switch (type) {
    case 'LOAD_LOCAL_STORE_SUCCEEDED':
      const dismissedItemsCache = payload.get('dismissed_activity_items') || {};

      return {
        ...state,
        dismissedItems: Immutable.Set(Object.keys(dismissedItemsCache)),
      };

    case 'SET_ACTIVITY_FILTER':
      return {
        ...state,
        activeFilter: payload.filter,
      };

    case 'FETCH_ALL_ACTIVITY_REQUESTED':
      return {
        ...state,
        [targetCollectionKey]: targetCollection.copyWith({ isFetching: true }),
      };

    case 'FETCH_ALL_ACTIVITY_SUCCEEDED':
      const responseCollection = payload.response.results
        .filter((activityItem) => !state.dismissedItems.has(activityItem.id))
        .map((activityItem) => [activityItem.id, new ActivityItem(activityItem)]);

      // // If the request included an LTE date, merge it into the current collection. If it did not
      // // include an LTE date, this should be the freshest data in the feed -- overwrite what we've got.
      const items: Immutable.OrderedMap<ID, ActivityItem> =
        targetCollection.updated && payload.resultsAfterDate
          ? targetCollection.items.merge(responseCollection)
          : Immutable.OrderedMap(responseCollection);

      let targetCollectionMergedContactsMap = targetCollection.mergedContactsMap;
      let targetCollectionSourceContactIDToMergedContactIDMap =
        targetCollection.sourceContactIDToMergedContactIDMap;
      let otherCollectionMergedContactsMap = otherCollection.mergedContactsMap;
      let otherCollectionSourceContactIDToMergedContactIDMap =
        otherCollection.sourceContactIDToMergedContactIDMap;

      if (state.deletedIDCacheToExpire.size > 0 || state.mergedIDCacheToExpire.size > 0) {
        state.mergedIDCacheToExpire.forEach((id) => {
          targetCollectionMergedContactsMap = targetCollectionMergedContactsMap.delete(id);
          otherCollectionMergedContactsMap = otherCollectionMergedContactsMap.delete(id);
        });

        // for each of the "source" contact IDs, remove the mapping of that ID to the merged contact ID
        state.deletedIDCacheToExpire.forEach((id) => {
          targetCollectionSourceContactIDToMergedContactIDMap =
            targetCollectionSourceContactIDToMergedContactIDMap.delete(id);
          otherCollectionSourceContactIDToMergedContactIDMap =
            otherCollectionSourceContactIDToMergedContactIDMap.delete(id);
        });
      }

      return {
        ...state,
        [targetCollectionKey]: targetCollection.copyWith({
          isFetching: false,
          updated: new Occasion(),
          items: items.sort((a, b) => b.date.unix - a.date.unix),
          canLoadMore: payload.response.total > payload.response.results.length,
          archivedContactIDs: targetCollection.archivedContactIDs.subtract(
            state.deletedIDCacheToExpire,
          ),
          mergedContactsMap: targetCollectionMergedContactsMap,
          sourceContactIDToMergedContactIDMap: targetCollectionSourceContactIDToMergedContactIDMap,
        }),
        [otherCollectionKey]: otherCollection.copyWith({
          archivedContactIDs: otherCollection.archivedContactIDs.subtract(
            state.deletedIDCacheToExpire,
          ),
          mergedContactsMap: otherCollectionMergedContactsMap,
          sourceContactIDToMergedContactIDMap: otherCollectionSourceContactIDToMergedContactIDMap,
        }),
        deletedIDCacheToExpire: Immutable.Set(),
        mergedIDCacheToExpire: Immutable.Set(),
      };

    case 'DELETE_ACTIVITY_ITEM_REQUESTED':
      return {
        ...state,
        all: state.all.copyWith({
          items: state.all.items.update(payload.id, (activityItem) =>
            activityItem?.copyWith({ status: 'DISMISSED', isDeleting: true }),
          ),
        }),
        filtered: state.filtered.copyWith({
          items: state.filtered.items.update(payload.id, (activityItem) =>
            activityItem?.copyWith({ status: 'DISMISSED', isDeleting: true }),
          ),
        }),
        dismissedItems: state.dismissedItems.add(payload.id),
      };

    case 'CLEAR_ACTIVITY_ITEM_FROM_STORE':
      return {
        ...state,
        all: state.all.copyWith({
          items: state.all.items.delete(payload.id),
        }),
        filtered: state.all.copyWith({
          items: state.filtered.items.delete(payload.id),
        }),
      };

    case 'DELETE_ACTIVITY_ITEM_SUCCEEDED':
      let nextActiveFilter = state.activeFilter;
      const nextAvailableFilters = state.availableFilters
        ?.map((filter) => {
          if (filter.type === payload.itemType) {
            const count = filter.count - 1;
            if (count === 0 && filter.type === nextActiveFilter) {
              nextActiveFilter = undefined;
            }
            return { ...filter, count: filter.count - 1 };
          }
          return filter;
        })
        .filter((filter) => filter.count > 0);

      return {
        ...state,
        all: state.all.copyWith({
          items: state.all.items.delete(payload.id),
        }),
        filtered: state.all.copyWith({
          items: state.filtered.items.delete(payload.id),
        }),
        availableFilters: nextAvailableFilters,
        activeFilter: nextActiveFilter,
      };

    case 'DELETE_ACTIVITY_ITEM_FAILED':
      return {
        ...state,
        all: state.all.copyWith({
          items: state.all.items.update(payload.id, (activityItem) =>
            activityItem?.copyWith({ status: 'ACTIVE', isDeleting: false }),
          ),
        }),
        filtered: state.all.copyWith({
          items: state.filtered.items.update(payload.id, (activityItem) =>
            activityItem?.copyWith({ status: 'ACTIVE', isDeleting: false }),
          ),
        }),
        dismissedItems: state.dismissedItems.remove(payload.id),
      };

    case 'FETCH_ALL_ACTIVITY_FILTERS_SUCCEEDED':
      return {
        ...state,
        availableFilters: payload.response,
      };

    // TODO: on create, we should increment the filter count for the new item's type
    // SSE dispatched event
    case 'ACTIVITY_ITEM_CREATED':
    case 'ACTIVITY_ITEM_UPDATED':
      if (payload?.channel === 'notification') {
        return state;
      }

      return {
        ...state,
        all: state.all.copyWith({
          items: state.all.items
            .set(payload.id, new ActivityItem(payload))
            .sort((a, b) => b.date.unix - a.date.unix),
        }),
        filtered:
          state.activeFilter && state.activeFilter === payload?.itemType
            ? state.all.copyWith({
                items: state.filtered.items
                  .set(payload.id, new ActivityItem(payload))
                  .sort((a, b) => b.date.unix - a.date.unix),
              })
            : state.filtered,
      };

    case 'MARK_ACTIVITY_AS_SEEN':
      return {
        ...state,
        seenItems: state.seenItems.union(state.all.items.keySeq()),
      };

    case 'CLEAR_UNSEEN_ACTIVITY':
      return {
        ...state,
        all: state.all.copyWith({
          items: state.all.items.map((activityItem) => activityItem?.copyWith({ seen: true })),
        }),
        filtered: state.filtered.copyWith({
          items: state.filtered.items.map((activityItem) => activityItem?.copyWith({ seen: true })),
        }),
      };

    case 'UPDATE_ACTIVITY_REQUESTED':
      if (payload.status === 'dismissed') {
        return {
          ...state,
          all: state.all.copyWith({
            isDeleting: true,
          }),
          filtered: state.filtered.copyWith({
            isDeleting: true,
          }),
        };
      }

      return state;

    case 'UPDATE_ACTIVITY_SUCCEEDED':
      if (payload.status === 'dismissed') {
        const typeToDismiss = payload.type;

        if (!typeToDismiss) {
          return {
            ...state,
            all: state.all.copyWith({
              items: [],
              isDeleting: false,
            }),
            filtered: state.filtered.copyWith({
              items: [],
              isDeleting: false,
            }),
            availableFilters: [],
          };
        }

        return {
          ...state,
          all: state.all.copyWith({
            items: state.all.items.filter((activityItem) => activityItem?.type !== typeToDismiss),
            isDeleting: false,
          }),
          filtered: state.filtered.copyWith({ items: [], isDeleting: false }),
          activeFilter: undefined,
          availableFilters: state.availableFilters.filter(
            (filter) => filter.type !== typeToDismiss,
          ),
        };
      }

    case 'UPDATE_ACTIVITY_FAILED':
      return {
        ...state,
        ...(payload?.initialCollectionsState || {}),
      };

    case 'UPDATE_RECONNECT_ACTIVITY_ITEM_PERSON_REQUESTED':
      const updateActivityItem = (activityItem: ActivityItem) => {
        const contactStatus = {
          ...(activityItem?.metadata?.contactStatus || {}),
          [payload.contactID]: payload.status,
        };

        return activityItem?.copyWith({
          metadata: { ...activityItem.metadata, contactStatus },
        });
      };

      return {
        ...state,
        all: state.all.copyWith({
          items: state.all.items.update(payload.activityItemID, updateActivityItem),
        }),
        filtered: state.filtered.copyWith({
          items: state.filtered.items.update(payload.activityItemID, updateActivityItem),
        }),
      };

    case 'FETCH_ALL_ACTIVITY_FAILED':
    default:
      return state;
  }
}
